import { useEffect, useState } from "react";
import { authAxios } from "../../utils/auth";
import { Card } from "../ui/card";
import { Users, Mail, TrendingUp, DollarSign, Send, MousePointerClick, RefreshCw, ArrowRightLeft } from "lucide-react";
import { Button } from "../ui/button";
import { toast } from "sonner";
import CommissionLedgerV2Panel from "./CommissionLedgerV2Panel";

const GOLD = "#C5A55A";

const SysEmailLabels = {
  affiliate_weekly_digest: "Weekly Digest",
  affiliate_payout_cleared: "Payout Cleared",
};

// Convert Command-Center range values to a days integer for these
// legacy endpoints. "total" is capped at 10 years for query efficiency.
const rangeToDays = (r) => ({ "7d": 7, "30d": 30, "90d": 90, "1y": 365, "total": 3650 }[r] || 30);
const rangeLabel = (r) => ({ "7d": "Last 7 days", "30d": "Last 30 days", "90d": "Last 90 days", "1y": "Last year", "total": "All time" }[r] || "Last 30 days");

export default function AffiliatesTab({ range = "30d" }) {
  const [kpis, setKpis] = useState(null);
  const [emailPerf, setEmailPerf] = useState([]);
  const [topEarners, setTopEarners] = useState([]);
  const [digestRuns, setDigestRuns] = useState([]);
  const [loading, setLoading] = useState(false);
  const days = rangeToDays(range);
  const label = rangeLabel(range);

  const load = async () => {
    setLoading(true);
    try {
      const [k, ep, te, dr] = await Promise.all([
        authAxios.get(`/analytics/affiliate/kpis?days=${days}`),
        authAxios.get(`/analytics/affiliate/email-performance?days=${days}`),
        authAxios.get(`/analytics/affiliate/top-earners?limit=20&days=${days}`),
        authAxios.get("/analytics/affiliate/digest-runs?limit=8"),
      ]);
      setKpis(k.data);
      setEmailPerf(ep.data.rows || []);
      setTopEarners(te.data.rows || []);
      setDigestRuns(dr.data.rows || []);
    } catch (err) {
      toast.error("Failed to load affiliate analytics");
    } finally {
      setLoading(false);
    }
  };

  // Refetch whenever the header range dropdown changes.
  useEffect(() => { load(); /* eslint-disable-line react-hooks/exhaustive-deps */ }, [days]);

  return (
    <section data-testid="affiliates-analytics-section" className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold text-white flex items-center gap-2">
          <Users className="w-4 h-4" style={{ color: GOLD }} /> Affiliate Program
        </h2>
        <Button
          size="sm"
          variant="outline"
          onClick={load}
          disabled={loading}
          className="border-white/10 text-white/60 hover:text-white gap-1.5"
          data-testid="refresh-affiliates-analytics"
        >
          <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} /> Refresh
        </Button>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3" data-testid="affiliate-kpis">
        <KpiCard icon={Users} label="Total Affiliates" value={kpis?.total_affiliates ?? "—"} testId="kpi-total-affiliates" />
        <KpiCard icon={TrendingUp} label={`Active (${label})`} value={kpis?.active_affiliates_30d ?? "—"} testId="kpi-active-30d" />
        <KpiCard icon={DollarSign} label={`Accrued (${label})`} value={kpis ? `$${(kpis.commissions_accrued_30d_usd || 0).toLocaleString()}` : "—"} testId="kpi-accrued-30d" />
        <KpiCard icon={DollarSign} label={`Paid Out (${label})`} value={kpis ? `$${(kpis.payouts_cleared_30d_usd || 0).toLocaleString()}` : "—"} testId="kpi-paid-30d" />
        <KpiCard icon={Mail} label="Weekly Digests Last Run" value={digestRuns[0] ? `${digestRuns[0].sent} sent` : "—"} testId="kpi-digest-last" />
      </div>

      {/* Sys email performance */}
      <Card className="bg-[#0a0a0f] border-white/5 p-5" data-testid="sys-email-performance-card">
        <h3 className="text-sm font-semibold text-white/90 mb-3 flex items-center gap-2">
          <Mail className="w-4 h-4" style={{ color: GOLD }} /> System Email Performance ({label})
        </h3>
        <div className="overflow-x-auto">
          <table className="w-full text-xs" data-testid="sys-email-performance-table">
            <thead className="text-white/40 uppercase tracking-wide">
              <tr>
                <th className="text-left py-2 px-2">Template</th>
                <th className="text-right py-2 px-2">Sent</th>
                <th className="text-right py-2 px-2">Delivered</th>
                <th className="text-right py-2 px-2">Opened</th>
                <th className="text-right py-2 px-2">Open %</th>
                <th className="text-right py-2 px-2">Clicked</th>
                <th className="text-right py-2 px-2">Click %</th>
                <th className="text-right py-2 px-2">Bounced</th>
                <th className="text-right py-2 px-2">Unsub</th>
              </tr>
            </thead>
            <tbody>
              {emailPerf.map((r) => (
                <tr key={r.slug} className="border-t border-white/[0.04] text-white/80" data-testid={`sys-email-row-${r.slug}`}>
                  <td className="py-2 px-2">{SysEmailLabels[r.slug] || r.slug}</td>
                  <td className="text-right py-2 px-2 font-mono">{r.sent.toLocaleString()}</td>
                  <td className="text-right py-2 px-2 font-mono">{r.delivered.toLocaleString()}</td>
                  <td className="text-right py-2 px-2 font-mono">{r.opened.toLocaleString()}</td>
                  <td className="text-right py-2 px-2 font-mono" style={{ color: GOLD }}>{r.open_rate}%</td>
                  <td className="text-right py-2 px-2 font-mono">{r.clicked.toLocaleString()}</td>
                  <td className="text-right py-2 px-2 font-mono" style={{ color: GOLD }}>{r.click_rate}%</td>
                  <td className="text-right py-2 px-2 font-mono text-red-400">{r.bounced}</td>
                  <td className="text-right py-2 px-2 font-mono text-orange-400">{r.unsubscribed}</td>
                </tr>
              ))}
              {emailPerf.length === 0 && (
                <tr><td colSpan="9" className="text-center text-white/30 py-6">No data</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </Card>

      {/* Weekly digest runs */}
      <Card className="bg-[#0a0a0f] border-white/5 p-5" data-testid="digest-runs-card">
        <h3 className="text-sm font-semibold text-white/90 mb-3 flex items-center gap-2">
          <Send className="w-4 h-4" style={{ color: GOLD }} /> Weekly Digest Runs
        </h3>
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead className="text-white/40 uppercase tracking-wide">
              <tr>
                <th className="text-left py-2 px-2">ISO Week</th>
                <th className="text-left py-2 px-2">Ran At</th>
                <th className="text-right py-2 px-2">Sent</th>
                <th className="text-right py-2 px-2">Skipped</th>
                <th className="text-right py-2 px-2">Eligible</th>
              </tr>
            </thead>
            <tbody>
              {digestRuns.map((r) => (
                <tr key={r.iso_week} className="border-t border-white/[0.04] text-white/80">
                  <td className="py-2 px-2 font-mono">{r.iso_week}</td>
                  <td className="py-2 px-2 text-white/50">{r.ran_at ? new Date(r.ran_at).toLocaleString() : "—"}</td>
                  <td className="text-right py-2 px-2 font-mono">{r.sent || 0}</td>
                  <td className="text-right py-2 px-2 font-mono">{r.skipped || 0}</td>
                  <td className="text-right py-2 px-2 font-mono">{r.eligible || 0}</td>
                </tr>
              ))}
              {digestRuns.length === 0 && (
                <tr><td colSpan="5" className="text-center text-white/30 py-6">No digest runs yet</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </Card>

      {/* Commission Ledger V2 — inline (same as /plog/commission_ledger_v2) */}
      <Card className="bg-[#0a0a0f] border-white/5 p-5" data-testid="commission-ledger-card">
        <h3 className="text-sm font-semibold text-white/90 mb-3 flex items-center gap-2">
          <ArrowRightLeft className="w-4 h-4" style={{ color: GOLD }} /> Commission Ledger ({label})
        </h3>
        <p className="text-[11px] text-white/40 mb-3">
          Click any row to inspect the commission record and jump to the Guardian queue when there&apos;s an open case.
        </p>
        <CommissionLedgerV2Panel range={range} />
      </Card>

      {/* Top earners */}
      <Card className="bg-[#0a0a0f] border-white/5 p-5" data-testid="top-earners-card">
        <h3 className="text-sm font-semibold text-white/90 mb-3 flex items-center gap-2">
          <MousePointerClick className="w-4 h-4" style={{ color: GOLD }} /> Top 20 Earning Affiliates ({label})
        </h3>
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead className="text-white/40 uppercase tracking-wide">
              <tr>
                <th className="text-left py-2 px-2">Name</th>
                <th className="text-left py-2 px-2">Email</th>
                <th className="text-left py-2 px-2">Aff ID</th>
                <th className="text-left py-2 px-2">Tier</th>
                <th className="text-right py-2 px-2">Commissions</th>
                <th className="text-right py-2 px-2">Rows</th>
              </tr>
            </thead>
            <tbody>
              {topEarners.map((r) => (
                <tr key={r.user_id} className="border-t border-white/[0.04] text-white/80" data-testid={`top-earner-${r.user_id}`}>
                  <td className="py-2 px-2">{r.name}</td>
                  <td className="py-2 px-2 text-white/50">{r.email}</td>
                  <td className="py-2 px-2 font-mono text-white/50">{r.aff_id}</td>
                  <td className="py-2 px-2">
                    <span className={`px-2 py-0.5 rounded text-[10px] ${r.tier === "PRO" ? "bg-yellow-500/15 text-yellow-400" : "bg-white/[0.06] text-white/60"}`}>{r.tier}</span>
                  </td>
                  <td className="text-right py-2 px-2 font-mono" style={{ color: GOLD }}>${r.total_usd.toLocaleString()}</td>
                  <td className="text-right py-2 px-2 font-mono text-white/60">{r.commission_count}</td>
                </tr>
              ))}
              {topEarners.length === 0 && (
                <tr><td colSpan="6" className="text-center text-white/30 py-6">No earning affiliates in this window</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </Card>
    </section>
  );
}

function KpiCard({ icon: Icon, label, value, testId }) {
  return (
    <Card className="bg-[#0a0a0f] border-white/5 p-4" data-testid={testId}>
      <div className="flex items-center gap-2 text-white/40 text-[10px] uppercase tracking-wide">
        <Icon className="w-3 h-3" />
        {label}
      </div>
      <div className="mt-2 text-xl font-mono text-white/90">{value}</div>
    </Card>
  );
}
